import React, { useEffect, useRef } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler
} from 'chart.js';

const columnBandPlugin = {
  id: 'columnBandPlugin',
  beforeDatasetsDraw(chart, args, options) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    if (!options || options.mode !== 'column') return;
    const currentIndex = options.currentIndex;
    if (currentIndex === null || currentIndex === undefined) return;
    const yScale = scales.y;
    if (!yScale) return;
    const prev = yScale.getPixelForValue(currentIndex - 0.5);
    const next = yScale.getPixelForValue(currentIndex + 0.5);
    const top = Math.min(prev, next);
    const height = Math.abs(next - prev);
    if (!Number.isFinite(top) || !Number.isFinite(height)) return;
    ctx.save();
    ctx.fillStyle = options.color || 'rgba(0,0,0,0.05)';
    ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, height);
    ctx.restore();
  }
};

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler, columnBandPlugin);

const LivermorePriceChart = ({ dataPoints, columnLabels, columnColors, mode = 'price', columnOrder = [], currentColumn = null }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return undefined;
    if (!dataPoints || dataPoints.length === 0) return undefined;

    const root = document.documentElement;
    const style = getComputedStyle(root);
    const textColor = style.getPropertyValue('--text-primary')?.trim() || '#111827';
    const gridColor = style.getPropertyValue('--border-color')?.trim() || 'rgba(0,0,0,0.08)';
    const tooltipBg = style.getPropertyValue('--card-bg')?.trim() || '#ffffff';
    const tooltipBorder = style.getPropertyValue('--border-color')?.trim() || '#e5e7eb';

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const isColumnMode = mode === 'column';
    const columnIndexMap = columnOrder.reduce((acc, key, index) => {
      const mappedIndex = columnOrder.length - 1 - index;
      acc[key] = mappedIndex;
      return acc;
    }, {});

    const ctx = chartRef.current.getContext('2d');
    const currentColumnIndex = isColumnMode && currentColumn && columnIndexMap[currentColumn] !== undefined
      ? columnIndexMap[currentColumn]
      : null;

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: dataPoints.map(point => point.label),
        datasets: [
          {
            data: dataPoints.map(point => ({
              x: point.label,
              y: isColumnMode ? columnIndexMap[point.column] : point.price,
              column: point.column,
              price: point.price,
              isPivot: point.isPivot
            })),
            borderWidth: 2,
            tension: 0.25,
            fill: false,
            clip: 8,
            pointRadius: (context) => {
              const raw = context?.raw;
              if (raw?.isPivot) return 9;
              return 8;
            },
            pointHoverRadius: (context) => {
              const raw = context?.raw;
              if (raw?.isPivot) return 13;
              return 12;
            },
            pointStyle: (context) => {
              const raw = context?.raw;
              return raw?.isPivot ? 'rectRot' : 'circle';
            },
            borderColor: (context) => {
              const raw = context?.p0?.raw || context?.raw;
              if (!raw?.column) return '#9ca3af';
              return columnColors[raw.column] || '#9ca3af';
            },
            pointBackgroundColor: (context) => {
              const raw = context?.raw;
              if (!raw?.column) return '#9ca3af';
              return columnColors[raw.column] || '#9ca3af';
            },
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2.5
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          columnBandPlugin: {
            mode: isColumnMode ? 'column' : 'price',
            currentIndex: currentColumnIndex,
            color: currentColumn ? `${columnColors[currentColumn] || '#9ca3af'}20` : 'rgba(0,0,0,0.05)'
          },
          tooltip: {
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            borderWidth: 1,
            titleColor: textColor,
            bodyColor: textColor,
            callbacks: {
              title: (items) => items?.[0]?.label || '',
              label: (context) => {
                const raw = context?.raw || {};
                const columnLabel = columnLabels[raw.column] || raw.column || 'Unknown';
                const price = Number.isFinite(raw.price) ? raw.price.toFixed(2) : raw.price;
                return `${columnLabel}: $${price}`;
              }
            }
          },
          datalabels: {
            display: false
          }
        },
        layout: {
          padding: {
            top: isColumnMode ? 16 : 0,
            bottom: isColumnMode ? 16 : 0
          }
        },
        scales: {
          x: {
            grid: {
              color: gridColor
            },
            ticks: {
              color: textColor,
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8
            }
          },
          y: {
            grid: {
              color: gridColor
            },
            ticks: {
              color: (context) => {
                if (!isColumnMode) return textColor;
                const index = Math.round(Number(context.tick?.value));
                const key = columnOrder[columnOrder.length - 1 - index];
                return key === currentColumn ? (columnColors[key] || textColor) : textColor;
              },
              font: (context) => {
                if (!isColumnMode) return { size: 12 };
                const index = Math.round(Number(context.tick?.value));
                const key = columnOrder[columnOrder.length - 1 - index];
                return key === currentColumn ? { size: 12, weight: '700' } : { size: 12 };
              },
              stepSize: isColumnMode ? 1 : undefined,
              callback: (value) => {
                if (!isColumnMode) return value;
                const index = Math.round(Number(value));
                const key = columnOrder[columnOrder.length - 1 - index];
                return columnLabels[key] || '';
              }
            },
            min: isColumnMode ? 0 : undefined,
            max: isColumnMode ? columnOrder.length - 1 : undefined
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [dataPoints, columnLabels, columnColors, mode, columnOrder, currentColumn]);

  return (
    <div className="livermore-chart">
      <canvas ref={chartRef} />
    </div>
  );
};

export default LivermorePriceChart;
